import { Dialog, DialogContent, DialogTitle, List, ListItem, Tooltip } from "@mui/material";
import { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { LoadTemplates } from '../../actions';
import { customStringComparator } from "../../helpers/custom-comparators";
import setToast from '../../helpers/toaster.helper';
import { CancelIcon, DuplicateIcon, EditIcon, TrashIcon } from "../../icon";
import { MediaModel, TemplateModel } from '../../models/templates.models';
import { UserModel } from "../../models/users.model";
import * as selector from "../../selectors/selectors";
import { TemplateService } from '../../services/TemplateService';
import { LocalStorage, UtilityService } from '../../services/UtilityService';
import ConfirmDialog from "../shared/confirm-dialog.component";
import { ActionLabels, ToasterTexts, appNote, buttonLabels, emptyLabels } from "../shared/constants";
import GridComponent from "../shared/grid";
import { MediaRenderer } from "../shared/grid/MediaRenderer";
import { ActionsRenderer } from "../shared/grid/actionsRenderer";
import { dateComparator } from "../shared/grid/grid.services";
import { MessageTagRenderer } from "../shared/grid/msgTagRendrer";
import { ToasterType } from '../shared/toaster/toaster.model';
import { UserService } from "../../services/UserService";

interface IProps {
    Media?: MediaModel
    SearchInput?: string;
    EditOptionHandler: (value?: TemplateModel) => void;
    DuplicateHandler: (value?: TemplateModel) => void;
    DeleteHandler?: (value?: TemplateModel) => void;
    IsPrivate: boolean;
    IsPhoneNumberLevel: boolean;
    onGridReady: (params: any) => void
}

const PrivateTemplates = (props: IProps) => {
    const dispatch = useDispatch()
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
    const Templates: TemplateModel[] = useSelector(selector.templates)
    const AllUsers: UserModel[] = useSelector(selector.allUsers)
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [mobileMenu, setMobileMenu] = useState<boolean>(false);
    const [isDeleted, setIsDeleted] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<TemplateModel>();
    const [RowData, setRowData] = useState<TemplateModel[]>([]);

    const TemplateColoumnDefs: ColDef[] = [
        {
            field: "Title",
            headerName: "Title",
            valueGetter: (params: ValueGetterParams) => {
                return UtilityService.replaceSpacesWithNBSP(params?.data?.Title);
            },
            wrapText: true,
            autoHeight: true,
        },
        {
            field: "Message",
            minWidth: IsMobileDevice ? 2 : 450,
            headerName: "Message",
            wrapText: true,
            autoHeight: true,
            comparator: customStringComparator,
            cellRendererSelector: (params: ICellRendererParams) => {
                if (params.data.Message) {
                    const Message = {
                        component: MessageTagRenderer,
                        params: {
                            Message: params.data.Message, ShowMediaIcon: !!params.data.Media?.length
                        }
                    }
                    return Message
                }
                return undefined
            },
        },
        {
            field: "Attachment",
            headerName: "Attachment",
            hide: IsMobileDevice,
            valueGetter: (params: ValueGetterParams) => {
                return !!params.data?.Media ? params.data?.Media.Name : "--"
            },
            cellRendererSelector: (params: ICellRendererParams) => {
                if (params.data) {
                    const media = !!params.data?.Media ? params.data?.Media : []
                    const ImageComponent = {
                        component: MediaRenderer,
                        params: {
                            media: media
                        }
                    }
                    return ImageComponent
                }
                return undefined
            },
            wrapText: true,
            autoHeight: true,
        },
        {
            field: "CreatedByID",
            headerName: "Created by",
            hide: props.IsPrivate || IsMobileDevice,
            valueGetter: (params: ValueGetterParams) => {
                const user = AllUsers.find(x => x.ID === params.data.CreatedByID)
                return `${user?.FirstName} ${user?.LastName}`.trim()
            },
            maxWidth: 125
        },
        {
            field: "UpdatedDateTime",
            headerName: "Last updated",
            valueGetter: (params: ValueGetterParams) => UtilityService.convertDateToDay(params.data.UpdatedDateTime),
            comparator: (valueA, valueB, nodeA, nodeB) => dateComparator(nodeA.data.UpdatedDateTime, nodeB.data.UpdatedDateTime),
            sort: !IsMobileDevice ? 'desc' : null,
            hide: IsMobileDevice,
            maxWidth: 140
        },

        {
            field: "Action",
            maxWidth: 40,
            headerName: "",
            pinned: "right",
            lockPinned: true,
            lockPosition: "right",
            cellClass: "ag-cell-actions-style",
            headerClass: "bg-color-secondary",
            cellRenderer: params => <ActionsRenderer MenuData={getMenuActions(params.data)}{...params} />,
            resizable: false,
            sortable: false,
            hide: IsMobileDevice
        }
    ]

    const getMenuActions = (data: TemplateModel) => {
        const currentUserId = LocalStorage.get("current-user")?.id;
        return [{
            Name: ActionLabels.EDIT,
            Icon: EditIcon,
            OnClick: () => props.EditOptionHandler((data as TemplateModel)),
            Hide: (data.IsPhoneNumberLevel || !data.IsPrivate) && UserService.hasUserRole() && data.CreatedByID !== currentUserId
        }, {
            Name: ActionLabels.DUPLICATE,
            Icon: DuplicateIcon,
            OnClick: () => props.DuplicateHandler(data),
        }, {
            Name: ActionLabels.DELETE,
            Icon: TrashIcon,
            OnClick: () => openConfirmDialog(data as TemplateModel),
            Hide: (data.IsPhoneNumberLevel || !data.IsPrivate) && UserService.hasUserRole() ? true : false
        }]
    }


    const onSelectionChanged = () => {

    }

    const openConfirmDialog = (template?: TemplateModel) => {
        setIsDeleted(true);
        setIsLoading(false);
        setMobileMenu(true);
        setSelectedRow(template);
    };

    const closeConfirmDialog = (isConfim: boolean) => {
        if (!!isConfim) {
            setIsLoading(true);
            let Template = Templates.find((x: TemplateModel) => x.ID === selectedRow?.ID)
            let Name = Template ? Template.Title : "";
            TemplateService.deleteTemplate(selectedRow?.ID)
                .then(() => {
                    setMobileMenu(false);
                    setIsLoading(false);
                    setIsDeleted(false);
                    setToast(ToasterTexts.TEMPLATE_DELETE_SUCCESS.replace('<Name>', Name), ToasterType.SUCCESS)
                    LoadTemplates(dispatch);
                }).catch((error: any) => {
                    setMobileMenu(false);
                    setIsLoading(false);
                    setIsDeleted(false);
                    setToast(ToasterTexts.SOMETHING_WENT_WRONG_MESSAGE, ToasterType.DANGER)
                })
        } else {
            setMobileMenu(false);
            setIsLoading(false);
            setIsDeleted(false);
        }
    };

    const handleRowClick = (row: TemplateModel) => {
        if (IsMobileDevice) {
            setMobileMenu(true);
            setSelectedRow(row);
        }
    }
    const closeMenu = () => {
        setMobileMenu(false);
    };

    useEffect(() => {
        if (Templates.length > 0)
            setRowData(Templates)
        else {
            setRowData([])
        }
    }, [Templates])

    const handleCellRowClick = (e: any, row: TemplateModel) => {
        if (!IsMobileDevice && e.colDef.field !== "Action") {
            const currentUserId = LocalStorage.get("current-user")?.id;
            if (!((row.IsPhoneNumberLevel || !row.IsPrivate) && UserService.hasUserRole() && row.CreatedByID !== currentUserId)) {
                props.EditOptionHandler(row)
            }
        }
    }

    return (
        <div className="d-flex flex-direction h-p-100 o-hidden justify-between">
            <GridComponent
                ColumnDefs={TemplateColoumnDefs}
                RowData={RowData.filter(x => !!props.IsPrivate ? x.IsPrivate === props.IsPrivate : !!props.IsPhoneNumberLevel ? x.IsPhoneNumberLevel === props.IsPhoneNumberLevel : !x.IsPhoneNumberLevel && !x.IsPrivate)}
                OnGridReady={props.onGridReady}
                OnSelectionChanged={onSelectionChanged}
                OnGridRowClick={handleRowClick}
                IsPaginationEnabled={!IsMobileDevice}
                EmptyLabel={emptyLabels.TEMPLATES}
                LockVisible={IsMobileDevice}
                OnGridCellRowClick={handleCellRowClick}
                ShowRowPointer={true}
            />
            {IsMobileDevice && UtilityService.isIPhone(navigator.userAgent) && <div className="mb-2" />}

            {
                isDeleted &&
                <ConfirmDialog
                    ButtonName={buttonLabels.DELETE}
                    onClose={closeConfirmDialog}
                    Show={isDeleted}
                    Text={appNote.TEMPLATE_DELETE}
                    Loading={isLoading}
                    IsMobileDevice={IsMobileDevice}
                ></ConfirmDialog>
            }

            {IsMobileDevice && mobileMenu && (
                <Dialog
                    fullWidth
                    maxWidth="sm"
                    onClose={closeMenu}
                    aria-labelledby="responsive-dialog-title"
                    open={true}
                    classes={{ paper: "br-2 dialogue-shadow w-p-70 bg-color-menu-bg" }}
                >
                    <DialogTitle className="pb-1">
                        <div
                            className="h1 color-display-title d-flex"
                            id="customized-dialog-title"
                        >
                            {selectedRow !== undefined &&
                                UtilityService.trimMessage(selectedRow?.Title, 12)}
                            <span className="ml-auto">
                                <Tooltip title="Close" placement="bottom">
                                    <CancelIcon
                                        className={"c-pointer"}
                                        onClick={closeMenu}
                                    />
                                </Tooltip>
                            </span>
                        </div>
                    </DialogTitle>
                    <DialogContent className={"mln-1 o-hidden"}>
                        <List className={"pt-0"}>
                            <ListItem className={"px-1 py-1 h2 color-menu-list-font-color"} button onClick={() => props.EditOptionHandler(selectedRow)}>
                                <EditIcon className={"mr-1"} />
                                <span className={"h4-regular color-menu-list-font-color"}>{ActionLabels.EDIT}</span>
                            </ListItem>
                            <ListItem className={"px-1 py-1 h2 color-menu-list-font-color"} button onClick={() => props.DuplicateHandler(selectedRow)}>
                                <DuplicateIcon className={"mr-1"} />
                                <span className={"h4-regular color-menu-list-font-color"}>{ActionLabels.DUPLICATE}</span>
                            </ListItem>
                            <ListItem className={"px-1 py-1 h2 color-menu-list-font-color"} button onClick={() => openConfirmDialog(selectedRow)}>
                                <TrashIcon className={"mr-1"} />
                                <span className={"h4-regular color-menu-list-font-color"}>{ActionLabels.DELETE}</span>
                            </ListItem>
                        </List>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    )
}

export default PrivateTemplates